import {
  BarChart3,
  Contact,
  Handshake,
  LayoutDashboard,
  ListChecks,
  Mail,
  FolderKanban,
  Package,
  Repeat,
  Target,
  Users,
} from "lucide-react";
import { useCanAccess, useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

import { cn } from "@/lib/utils";

import { prefetchRoute } from "./routePrefetch";
import { goToShortcutFor } from "./shortcuts";

// Venstre ikon-rail. Alltid kollapset (kun ikon + liten etikett). Det faste
// kundepanelet ved siden av er fjernet: det tok ~280 px på alle sider og viste
// kundene to ganger på Kunder. Kunder nås nå via Kunder, ⌘K og «G K», og
// prosjektene, som før bare lå i panelets tre, har fått eget menypunkt.
const Header = () => {
  const translate = useTranslate();
  const location = useLocation();
  // Alle ser Rapporter (Salg-fanen); resten av fanene er bare for
  // administrator (pipeline pakke 6).
  const { canAccess: canSeeReports } = useCanAccess({
    resource: "reports",
    action: "list",
  });
  const { canAccess: canSeeAgreements } = useCanAccess({
    resource: "agreements",
    action: "list",
  });

  const allNavItems = [
    {
      to: "/",
      label: translate("ra.page.dashboard"),
      icon: LayoutDashboard,
      match: "/",
    },
    {
      to: "/work_items",
      label: translate("resources.work_items.name", { smart_count: 2 }),
      icon: ListChecks,
      match: "/work_items/*",
    },
    {
      to: "/companies",
      label: translate("resources.companies.name", { smart_count: 2 }),
      icon: Users,
      match: "/companies/*",
    },
    {
      to: "/contacts",
      label: translate("resources.contacts.name", { smart_count: 2 }),
      icon: Contact,
      match: "/contacts/*",
    },
    {
      to: "/projects",
      label: translate("resources.projects.name", { smart_count: 2 }),
      icon: FolderKanban,
      match: "/projects/*",
    },
    {
      to: "/prospects",
      label: translate("crm.prospects.title", { _: "Prospekter" }),
      icon: Target,
      match: "/prospects/*",
    },
    {
      to: "/deals",
      label: translate("crm.sales.title", { _: "Pipeline" }),
      icon: Handshake,
      match: "/deals/*",
    },
    {
      to: "/products",
      label: translate("resources.products.name", { smart_count: 2 }),
      icon: Package,
      match: "/products/*",
    },
    {
      to: "/agreements",
      label: translate("crm.agreements.nav", { _: "Avtaler" }),
      icon: Repeat,
      match: "/agreements/*",
    },
    {
      to: "/reports",
      label: translate("crm.reports.title", { _: "Rapporter" }),
      icon: BarChart3,
      match: "/reports/*",
    },
    {
      to: "/email",
      label: translate("crm.email.title_page", { _: "E-post" }),
      icon: Mail,
      match: "/email/*",
    },
  ];
  const navItems = allNavItems.filter(
    (item) =>
      (item.to !== "/reports" || canSeeReports) &&
      (item.to !== "/agreements" || canSeeAgreements),
  );

  const isActive = (item: { to: string; match: string }) =>
    item.to === "/"
      ? !!matchPath("/", location.pathname)
      : !!matchPath(item.match, location.pathname);

  return (
    <aside className="hidden h-full w-[var(--sidebar-width-icon)] shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground md:flex">
      <nav
        aria-label={translate("crm.navigation.label", { _: "Hovedmeny" })}
        className="flex flex-1 flex-col gap-1 overflow-y-auto p-2"
      >
        {navItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            title={
              goToShortcutFor(item.to)
                ? `${item.label} (G ${goToShortcutFor(item.to)!.toUpperCase()})`
                : item.label
            }
            aria-current={isActive(item) ? "page" : undefined}
            onPointerEnter={() => prefetchRoute(item.to)}
            onFocus={() => prefetchRoute(item.to)}
            className={cn(
              "flex flex-col items-center gap-1 rounded-md px-1 py-2 text-center text-[10px] leading-tight transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isActive(item)
                ? "bg-sidebar-accent font-medium text-primary"
                : // Samme nedtoning som filtervalg og kundepanelet, i stedet
                  // for den gamle /70-gjennomsiktigheten: en alfaverdi gir
                  // ulik valør alt etter hva som ligger bak, og skulle uansett
                  // si det samme som resten av appen sier med én farge.
                  "text-inactive-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            )}
          >
            <item.icon className="h-5 w-5" />
            <span className="whitespace-normal">{item.label}</span>
          </Link>
        ))}
      </nav>
    </aside>
  );
};

export default Header;
